import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser } from '../api';
import Cookies from 'js-cookie';
import styles from './AuthForm.module.css';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      const response = await loginUser({ email, password });
      const { token, userId, cartId } = response.data;

      if (!token || !userId) {
          throw new Error("Login response missing token or user ID.");
      }

      const userObject = { id: userId /* , email: email */ };
      login(userObject, token);

      if (cartId) {
        Cookies.set('cartId', cartId, { expires: 7, path: '/', secure: true, sameSite: 'strict' });
        console.log('Cart ID set:', cartId);
      }

      console.log('User logged in with ID:', userId);

      navigate('/cart');
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError(err.response.data.message || err.response.data.error || 'Invalid email or password');
      } else {
        console.error('Login error:', err.message || err.response || err);
        setError(err.message || 'Login failed. Please try again later.');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className={`max-w-md w-full p-8 md:p-10 space-y-8 ${styles.formContainer}`}>
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Sign in to ShopEase
          </h2>
        </div>
        {error && <div className={`${styles.errorMessage} text-sm`}>{error}</div>}
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <input type="hidden" name="remember" defaultValue="true" />
          <div className="rounded-md -space-y-px">
            <div>
              <label htmlFor="email-address" className={`block text-sm font-medium text-gray-700 mb-1 ${styles.inputLabel}`}>
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={`relative block w-full appearance-none rounded-md border px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:outline-none sm:text-sm ${styles.inputField}`}
                placeholder="Email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
              />
            </div>
            <div className="pt-4">
              <label htmlFor="password" className={`block text-sm font-medium text-gray-700 mb-1 ${styles.inputLabel}`}>
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className={`block w-full appearance-none rounded-md border px-3 py-2 pr-10 text-gray-900 placeholder-gray-500 focus:z-10 focus:outline-none sm:text-sm ${styles.inputField}`}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end text-sm">
            <div className="text-sm">
              <a href="/forgot-password" className={`font-medium text-blue-600 hover:text-blue-500 ${styles.authLink}`}>
                Forgot your password?
              </a>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className={`group relative flex w-full justify-center rounded-md border border-transparent bg-blue-600 py-3 px-4 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${styles.primaryButton}`}
            >
              Sign in
            </button>
          </div>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          Not a member?{' '}
          <a href="/register" className={`font-medium text-blue-600 hover:text-blue-500 ${styles.authLink}`}>
            Sign Up
          </a>
        </div>
      </div>
    </div>
  );
}

export default Login;
